import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { isPast } from 'date-fns';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCompletePathwayProgress,
  useCountdownTimer,
  useDelayTimer,
  useLanguagePairScore,
} from 'src/hooks';
import { RestrictToAuth } from 'src/utilityComponents';
import { AutoPlayProvider } from 'src/context/AutoPlayContext';
import { AudioProvider, useAudioLengthInMs, useAudioPlayback } from 'src/context/AudioContext';
import { LearningQueueApi, MeApi, MemriseApiError, TrackingApi } from 'src/api';
import { saveLastLanguagePair } from 'src/utils/language/saveLastLanguagePair';
import DevTools from 'src/apps/Tools/DevTools';
import { FollowLocals } from 'src/apps/FollowLocals';
import {
  rememberShownVideo,
  shownVideoFromLearnable,
  videoSrcFromCard,
  type ShownSessionVideo,
} from 'src/apps/FollowLocals/sessionVideos';
import { useFollowLocalsOffer } from 'src/apps/FollowLocals/useFollowLocalsOffer';
import { getFollowLocalsGate } from 'src/apps/FollowLocals/followLocalsGate';
import {
  getLanguagePairEntity,
  getUserEntity,
  LEARNING_SESSION_COMPLETED_SCHEMA,
  LEARNING_SESSION_STARTED_SCHEMA,
} from 'src/analytics/snowplow';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import useSafePush from 'src/hooks/useSafePush';
import { useActivityCheck } from 'src/hooks/useActivityCheck';
import { FeatureTokenType } from 'src/api/monetisation/types';
import { FeatureUpsellModal } from 'src/apps/Monetisation/FeatureUpsellModal';
import { BlobExperienceUpsell } from 'src/apps/Monetisation/BlobExperienceUpsell';
import { useReviewClassicBlobExperience } from 'src/apps/Monetisation/BlobExperienceUpsell/useReviewClassicBlobExperience';
import {
  getCanonicalSessionType,
  PresentationCard,
  SessionSourceType,
  SessionType,
  WebSessionType,
} from 'src/api/learningSessions/types';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';
import {
  MY_WORDS_INTRO_STORAGE_KEY,
  SHOULD_DISPLAY_MY_ACTIVITIES_INTRO_STORAGE_KEY,
  SHOW_REPETITION_INTRO_MODAL_STORAGE_KEY,
} from 'src/constants/storageKeys.const';
import { DailyNewWordsResponse, PointsResponse, ProgressResponse } from 'src/api/me/types';
import { ROUTES } from 'src/constants/routes.const';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { ParseIntError, safeParseInt } from 'src/utils/numbers';
import { useExperienceContext } from 'src/context/experience';
import { useEligibility } from 'src/hooks/useEligibility';
import { useSavedLearnables } from 'src/hooks/useSavedLearnables';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import { recordBlobLearningSessionCompletion } from 'src/apps/Path/BlobPronunciationQueue/queue';
import { twentyFourHoursAgo } from 'src/apps/DashboardFrame/utils/twentyFourHoursAgo';
import { practiceKey } from 'src/apps/DashboardFrame/useBlobNavbarCounts';
import { getAudio } from '../learnableCards/utils';
import { BaseSession } from '../BaseSession';
import { useSessionStore } from '../../hooks';
import { useCompletedBlobWordlists } from '../../hooks/useCompletedBlobWordlists';
import { StandardSession, StandardSessionProps } from '../../StandardSession';
import { BlobSession, isBlobSessionType } from '../../BlobSession';
import { LoadingSkeleton as BlobSessionLoadingSkeleton } from '../../BlobSession/LoadingSkeleton';
import { BLOB_TRACKING_DATA } from '../../BlobSession/tracking';
import { SpeedReview } from '../SpeedReview';
import { EndOfSession } from '../../EndOfSession';
import { recordCalendarLearnCompletion } from '../../EndOfSession/CalendarReminder/sessionCompletions';
import { getDelayLengthInMs, getLearningSessionType } from './utils';

const msgs = defineMessages({
  errorTitle: { id: 'learning_session_error_generic_header' },
  errorDescription: { id: 'resetprogress_toastbanner_error_title' },
});

export const TEST_MIN_SPEED_DELAY_TIMER_DURATION_IN_MS = 1000;
export const TEST_MIN_DELAY_TIMER_DURATION_IN_MS = 1500;
export const TEST_MAX_DELAY_TIMER_DURATION_IN_MS = 10000;
export const TEST_TIMER_DURATION_IN_MS = 6000;

type SessionContentProps = {
  sessionSourceId: number;
  sessionId: string;
  sessionType: WebSessionType;
  sessionSourceType: SessionSourceType;
};

const LANGUAGE_PAIR_SOURCE_TYPES: SessionSourceType[] = [
  'scenario',
  'language_pair',
  'user_path_blob',
];

const getScenarioId = (sessionSourceType: SessionSourceType, sessionSourceId: number) =>
  sessionSourceType === 'scenario' ? sessionSourceId : null;

const isPastNextDate = (nextDate: string | null | undefined) =>
  typeof nextDate === 'string' && isPast(new Date(nextDate));

export const SessionContent: FC<SessionContentProps> = ({
  sessionSourceId,
  sessionId,
  sessionType,
  sessionSourceType,
}) => {
  const { experienceType, isLoaded: experienceLoaded } = useExperienceContext();

  const {
    analyticsSessionId,
    languagePairId,
    isDoneUpdatingProgress,
    levelName,
    sessionSourceName,
    levelId,
    learnablesWithProgress,
    learnablesWithProgressLength,
    numItemsDueForReview,
    numCorrectAnswers,
    numIncorrectAnswers,
    onTestContinue,
    sendCurrentLearningProgress: sendCurrentLearningProgressBase,
    currentLearnableProgressPreferences,
    learningSession,
    learningSessionDispatch,
    onItemIgnored,
    onItemSkipped,
    onItemDifficultyChanged,
    userScenarioCompleted,
    currentLearnableAttempts,
    ignoredItemsCount,
    markPreviousAnswerCorrect,
  } = useSessionStore(sessionType, sessionSourceType, sessionSourceId, sessionId);

  const { safePush } = useSafePush();
  const { query } = useRouter();

  const [pointsBefore, setPointsBefore] = useState<PointsResponse>();
  const [progressBefore, setProgressBefore] = useState<ProgressResponse>();
  const [dailyNewWordsBefore, setDailyNewWordsBefore] = useState<DailyNewWordsResponse>();

  const languagePairFromQuery = useMemo(() => {
    const raw = query.language_pair_id;
    if (typeof raw !== 'string') {
      return null;
    }
    const parsed = safeParseInt(raw);
    return parsed instanceof ParseIntError ? null : parsed;
  }, [query.language_pair_id]);

  // Session payload is the source of truth (local IDs differ from staging).
  // Fall back to the URL, then last-used localStorage, so the score fetch can
  // start before session_source_info arrives.
  const languagePairID = languagePairId ?? languagePairFromQuery ?? getLanguagePairId();
  // Path queries are keyed by the language pair the rest of the app uses (URL /
  // session source), not the session payload which can differ locally vs staging.
  const pathwayLanguagePairId =
    (sessionSourceType === 'language_pair' ? sessionSourceId : null) ??
    languagePairFromQuery ??
    getLanguagePairId() ??
    languagePairId;
  const { user } = useUserContext();
  const intl = useIntl();
  const {
    eligibility,
    isEligibleForPathways,
    isLoading: eligibilityLoading,
  } = useEligibility(languagePairID);
  const canShowBlobSession = isBlobSessionType(sessionType);
  const isBlobSession = canShowBlobSession && !!eligibility?.eligibleForBlobExperience;
  const completedWordlists = useCompletedBlobWordlists({
    languagePairId: pathwayLanguagePairId,
    sessionId,
    sessionSourceType,
    sessionSourceId,
    enabled:
      isBlobSession && (sessionType === SessionType.LEARN || sessionType === SessionType.MIXED),
    hasFinished: learningSession.stage === 'summary' && isDoneUpdatingProgress,
  });
  const isWaitingForEligibility = canShowBlobSession && eligibilityLoading;
  // Don't flash classic blue session chrome while blob layout is pending or confirmed.
  const hideClassicSessionChrome = canShowBlobSession && (isWaitingForEligibility || isBlobSession);
  const experienceTrackingData = isBlobSession ? BLOB_TRACKING_DATA : undefined;
  const isEligibleForBlobExperience = !!eligibility?.eligibleForBlobExperience;
  // Saved words only jump the queue for Pro learners, so only theirs are labelled as such
  const canLearnSavedFirst = isEligibleForBlobExperience && !!user?.isPro;
  const { isSaved, isLoading: isLoadingSavedLearnables } = useSavedLearnables(
    canLearnSavedFirst ? (languagePairID ?? null) : null,
  );
  const { languagePair, isLoading: languagePairLoading } = useCurrentLanguagePair(languagePairID);
  useEffect(() => {
    if (
      isBlobSession &&
      languagePair?.supportsPronunciation === true &&
      user &&
      pathwayLanguagePairId !== null &&
      learningSession.stage === 'summary' &&
      isDoneUpdatingProgress
    ) {
      recordBlobLearningSessionCompletion(
        user.id,
        pathwayLanguagePairId,
        analyticsSessionId || String(sessionId),
        sessionType,
      );
    }
  }, [
    analyticsSessionId,
    isBlobSession,
    isDoneUpdatingProgress,
    languagePair?.supportsPronunciation,
    learningSession.stage,
    pathwayLanguagePairId,
    sessionId,
    sessionType,
    user,
  ]);
  const shownVideosRef = useRef<ShownSessionVideo[]>([]);
  if (learningSession.stage === 'card') {
    const src = videoSrcFromCard(learningSession.card.data);
    if (src) {
      shownVideosRef.current = rememberShownVideo(
        shownVideosRef.current,
        shownVideoFromLearnable(learningSession.learnableWithProgress.learnable, src),
      );
    }
  }
  const followLocals = useFollowLocalsOffer(
    sessionType === SessionType.LEARN &&
      learningSession.stage === 'summary' &&
      isDoneUpdatingProgress,
    getFollowLocalsGate({
      experienceLoaded,
      experienceType,
      eligibilityLoading,
      isEligibleForPathways,
      languagePairLoading,
      targetLanguageLocale: languagePair?.targetLanguageLocale,
    }),
    shownVideosRef.current.length > 0,
  );

  // Fetched in parallel with the session payload so it's ready by the time the first
  // presentation card renders, avoiding a visible example-sentence reorder mid-session.
  const { score: userScore } = useLanguagePairScore(languagePairID, {
    enabled: sessionType !== SessionType.SPEED_REVIEW,
  });

  const pathwayNodeId = useMemo(() => {
    const raw = query.pathway_node_id;
    if (typeof raw !== 'string') {
      return null;
    }
    const parsed = safeParseInt(raw);
    return parsed instanceof ParseIntError ? null : parsed;
  }, [query.pathway_node_id]);

  const { completePathwayProgress } = useCompletePathwayProgress({
    languagePairId: pathwayLanguagePairId,
    pathwayNodeId,
    pathwayCheckpointId: null,
    // Path vocab sessions are classic learn sessions; without pathway_node_id we
    // still credit the current path node when it is vocab.
    sessionType: sessionType === SessionType.LEARN ? 'vocab' : null,
    learnSessionId: sessionType === SessionType.LEARN ? sessionId : undefined,
  });

  useEffect(() => {
    if (learningSession.stage !== 'summary' || !isDoneUpdatingProgress) {
      return;
    }
    if (
      sessionType === SessionType.LEARN &&
      user?.dateJoined &&
      twentyFourHoursAgo(Date.parse(user.dateJoined)) &&
      pathwayLanguagePairId !== null
    ) {
      recordCalendarLearnCompletion(user.id, pathwayLanguagePairId, sessionId, pathwayNodeId);
    }
    if (pathwayNodeId === null && sessionType !== SessionType.LEARN) {
      return;
    }

    void completePathwayProgress().catch(() => {
      // Path return still works via ?back=; user can retry the node if needed.
    });
  }, [
    completePathwayProgress,
    isDoneUpdatingProgress,
    learningSession.stage,
    pathwayNodeId,
    sessionType,
    user,
    sessionId,
    pathwayLanguagePairId,
  ]);

  const blobReview = useReviewClassicBlobExperience(sessionType === SessionType.REVIEW);

  /* istanbul ignore next: please test in WEBSITE-7219 */
  const tokenType: FeatureTokenType | null = useMemo(() => {
    switch (sessionType) {
      case SessionType.REVIEW:
        return blobReview.isBlobExperience ? null : 'review_classic';
      case SessionType.SPEED_REVIEW:
        return 'review_speed';
      case SessionType.DIFFICULT_WORDS:
        return 'review_difficult_words';
      default:
        return null;
    }
  }, [blobReview.isBlobExperience, sessionType]);

  const {
    isLoading: isLoadingActivity,
    isError: isActivityError,
    isLocked,
    onActivityEngagement,
  } = useActivityCheck({
    tokenType,
  });
  /* istanbul ignore next: please test in WEBSITE-7219 */
  const activityError = isActivityError
    ? {
        header: msgs.errorTitle,
        description: msgs.errorDescription,
      }
    : null;
  /* istanbul ignore next: please test in WEBSITE-7219 */
  const isDataLoaded =
    learningSession.stage !== 'loading' &&
    !isLoadingActivity &&
    !blobReview.isLoading &&
    !activityError &&
    // Wait for eligibility so the session doesn't swap UIs after the first card renders
    !isWaitingForEligibility &&
    // Wait for saved words so the first card's "Added from Saved" label and tracking are final
    !isLoadingSavedLearnables;

  /* istanbul ignore next: please test in WEBSITE-7219 */
  useEffect(() => {
    if (learningSession.stage === 'card' && learningSession.card.responseState === 'Processing') {
      onActivityEngagement();
      if (blobReview.isFreeBlob) {
        blobReview.consumeReviewedWord(learningSession.learnableWithProgress.learnable.id);
      }
    }
  }, [blobReview, learningSession, onActivityEngagement]);

  const sendCurrentLearningProgress = useCallback(async () => {
    try {
      return await sendCurrentLearningProgressBase();
    } catch (e) {
      /* istanbul ignore else: unexpected error fallback, no need to test */
      if ([401, 403].includes((e as MemriseApiError).httpStatusCode)) {
        await safePush('/signin/');
      } else {
        throw e;
      }
    }
  }, [sendCurrentLearningProgressBase, safePush]);

  useEffect(() => {
    if (LANGUAGE_PAIR_SOURCE_TYPES.includes(sessionSourceType) && !languagePairID) {
      void safePush(ROUTES.dashboard);
    }
  }, [languagePairID, safePush, sessionSourceType]);

  const currentLearnableWithProgress =
    (learningSession.stage === 'card' && learningSession.learnableWithProgress) || null;

  const answerAudio: null | string = useMemo(
    () => (currentLearnableWithProgress ? getAudio(currentLearnableWithProgress.learnable) : null),
    [currentLearnableWithProgress],
  );

  // This hook is not an ideal solution
  // - It is susceptible to bugs on Firefox where audio length is measured wrong for some files
  // - We'd prefer a callback API
  const durationInMs = useAudioLengthInMs(answerAudio);
  const { play: playAnswerAudio, stopAll: stopAllAudio } = useAudioPlayback(answerAudio);

  // Initial SpeedReview timer code
  const {
    percentageTimeLeft: percentageTestTimeLeft,
    restartTimer: restartTestTimer,
    resetTimer: resetTestTimer,
    pauseTimer: pauseTestTimer,
  } = useCountdownTimer({
    durationInMs: TEST_TIMER_DURATION_IN_MS,
    disabled: sessionType !== SessionType.SPEED_REVIEW,
  });

  const onFeedbackFinished = useCallback(() => {
    /* istanbul ignore next: too difficult to test */
    if (learningSession.stage !== 'card') return;

    // If the timer runs out when the feedback has already been transitioned, dont reset answer again
    const answeredAndProcessed = ['Correct', 'Incorrect', 'NearlyCorrect'].includes(
      learningSession.card.responseState,
    );
    if (answeredAndProcessed) {
      stopAllAudio();
      learningSessionDispatch({ type: 'RESET_ANSWER' });
      // Transition to next learnable with progress
      onTestContinue();
    }
  }, [
    onTestContinue,
    learningSessionDispatch,
    stopAllAudio,
    learningSession.stage,
    learningSession.card,
  ]);

  const { restartTimer: restartDelayTimer, cancelTimer: cancelDelayTimer } = useDelayTimer(
    getDelayLengthInMs(durationInMs, sessionType),
    onFeedbackFinished,
  );

  const title = levelName ? sessionSourceName + ' - ' + levelName : sessionSourceName || '';

  const currentCard =
    learningSession.stage === 'card' && learningSession.card ? learningSession.card.card : null;

  const growthLevel =
    learningSession.stage === 'card' && learningSession.card
      ? learningSession.card.growthLevel
      : null;

  // Handle timer changes after correctness returned
  useEffect(() => {
    if (learningSession.stage !== 'card') return;

    const { responseState } = learningSession.card;
    if (responseState === 'Processing') {
      // Handle speed review timer
      pauseTestTimer();
      resetTestTimer();

      void playAnswerAudio();

      restartDelayTimer();
    }
  }, [
    learningSession.stage,
    learningSession.card,
    playAnswerAudio,
    pauseTestTimer,
    resetTestTimer,
    restartDelayTimer,
  ]);

  // Fail test if user runs out of time on speed review
  const outOfTime = percentageTestTimeLeft < 1;

  useEffect(() => {
    if (outOfTime) {
      // MemLearning doesn't have an API for 'fail test'/'mark as
      // timer expired' so we use this function to achieve the
      // same effect
      learningSessionDispatch({ type: 'ANSWER_TEST', answer: '' });
    }
  }, [outOfTime, learningSessionDispatch]);

  const onIntroCountdownFinished = useCallback(() => {
    restartTestTimer();
  }, [restartTestTimer]);

  useEffect(() => {
    // Pause timer if loading or on summary state
    // Restart it otherwise
    if (learningSession.stage !== 'card') {
      pauseTestTimer();
      resetTestTimer();
    } else {
      restartTestTimer();
    }
  }, [
    learningSession.stage,
    pauseTestTimer,
    resetTestTimer,
    restartTestTimer,
    currentLearnableWithProgress, // needed so the timer restarts for each new item
  ]);

  /* istanbul ignore next: default void function */
  let progressSession = () => {};
  let sessionPercentComplete = 0;
  if (learningSession.stage === 'card') {
    sessionPercentComplete = learningSession.session.percentComplete;
    progressSession = () => {
      stopAllAudio();
      (currentCard as PresentationCard).onContinue();
    };
  }

  useEffect(() => {
    if (LANGUAGE_PAIR_SOURCE_TYPES.includes(sessionSourceType) && languagePairId) {
      saveLastLanguagePair(languagePairId);
    }
  }, [languagePairId, sessionSourceType]);
  /* istanbul ignore next: please test in WEBSITE-7219 */
  const loadProgressData = useCallback(async (languagePairID: number) => {
    const [pointsBeforeData, progressBeforeData] = await Promise.all([
      await MeApi.getPoints(languagePairID),
      await MeApi.getProgress(languagePairID),
    ]);
    setPointsBefore(pointsBeforeData);
    setProgressBefore(progressBeforeData);
  }, []);

  // Snapshot of today's new words before any progress is sent, so the end of session can tell
  // whether this session is the one that reached the daily goal
  const hasRequestedDailyNewWordsBefore = useRef(false);
  useEffect(() => {
    if (!isBlobSession || !languagePairID || hasRequestedDailyNewWordsBefore.current) return;
    hasRequestedDailyNewWordsBefore.current = true;
    MeApi.getDailyNewWords(languagePairID)
      .then(setDailyNewWordsBefore)
      .catch(() => {});
  }, [isBlobSession, languagePairID]);

  // The blob navbar's review count only catches up once the session's progress is sent
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isBlobSession || !languagePairID || !isDoneUpdatingProgress) return;
    void queryClient.invalidateQueries({ queryKey: practiceKey(languagePairID) });
  }, [isBlobSession, languagePairID, isDoneUpdatingProgress, queryClient]);

  useEffect(() => {
    // Waits for eligibility so the event knows which session UI is showing
    if (learnablesWithProgressLength > 0 && !isWaitingForEligibility) {
      const scenarioId = getScenarioId(sessionSourceType, sessionSourceId);
      if (languagePairID && user?.id) {
        trackSelfDescribingEvent({
          event: {
            schema: LEARNING_SESSION_STARTED_SCHEMA,
            data: {
              learning_session_id: sessionId,
              scenario_id: scenarioId,
              num_items_for_review: numItemsDueForReview,
              num_session_items: learnablesWithProgressLength,
              learning_session_type: getLearningSessionType(sessionType),
            },
          },
          context: [
            getUserEntity(user.id, user.isPro, user.dateJoined, user.username),
            getLanguagePairEntity(languagePairID),
          ],
        });
        /* istanbul ignore next: analytics */
        if (sessionType === 'learn') {
          void loadProgressData(languagePairID);
        }

        /* istanbul ignore next: analytics */
        void TrackingApi.trackUserAction(
          'learning_session_started',
          'learning_session',
          languagePairID,
          !!user?.isPro,
          {
            ...experienceTrackingData,
            session_id: analyticsSessionId,
            session_type: getCanonicalSessionType(sessionType),
            page_screen_name: String(scenarioId),
            ...(query?.source && { activity_source: String(query.source) }),
            ...(query?.path_stone && { path_stone: String(query.path_stone) }),
          },
        );
      }
    }
  }, [
    analyticsSessionId,
    levelId,
    sessionId,
    learnablesWithProgressLength,
    numItemsDueForReview,
    sessionType,
    sessionSourceId,
    sessionSourceType,
    languagePairID,
    user,
    intl,
    query?.source,
    query?.path_stone,
    loadProgressData,
    isWaitingForEligibility,
    experienceTrackingData,
  ]);

  const { error, progressPreferences } = learningSession.session;

  useEffect(() => {
    if (learningSession.stage !== 'summaryLoading') return;
    /* istanbul ignore else: empty else branch */
    if (languagePairID && user?.id) {
      /* istanbul ignore else: empty else branch */
      trackSelfDescribingEvent({
        event: {
          schema: LEARNING_SESSION_COMPLETED_SCHEMA,
          data: {
            learning_session_id: sessionId,
            scenario_id: getScenarioId(sessionSourceType, sessionSourceId),
            num_session_items: learnablesWithProgressLength,
            learning_session_type: getLearningSessionType(sessionType),
          },
        },
        context: [
          getUserEntity(user.id, user.isPro, user.dateJoined, user.username),
          getLanguagePairEntity(languagePairID),
        ],
      });
      /* istanbul ignore next: analytics */
      void TrackingApi.trackUserAction(
        'learning_session_completed',
        'learning_session',
        languagePairID,
        !!user?.isPro,
        {
          ...experienceTrackingData,
          session_id: analyticsSessionId,
          session_type: getCanonicalSessionType(sessionType),
          page_screen_name: String(getScenarioId(sessionSourceType, sessionSourceId)),
          ...(query?.source && { activity_source: String(query.source) }),
          ...(query?.path_stone && { path_stone: String(query.path_stone) }),
        },
      );
    }
  }, [
    analyticsSessionId,
    sessionId,
    learningSession.stage,
    learnablesWithProgressLength,
    levelId,
    sessionType,
    learningSession?.summary?.endOfSessionPoints,
    sessionSourceId,
    sessionSourceType,
    user,
    languagePairID,
    query?.source,
    query?.path_stone,
    intl,
    experienceTrackingData,
  ]);

  const [userHadNoWords, setUserHadNoWords] = useState(false);
  useEffect(() => {
    if (user && user.statistics.wordsLearnt === 0) {
      setUserHadNoWords(true);
    }
  }, [user]);

  useEffect(() => {
    if (learningSession.stage === 'summary' && userHadNoWords) {
      const firstWordLearned = learningSession.summary.summaryItems.some(i => i.growthLevel >= 6);

      if (firstWordLearned && experienceType === '2024') {
        setLocalStorageEntry(MY_WORDS_INTRO_STORAGE_KEY, 'true');
        setLocalStorageEntry(SHOULD_DISPLAY_MY_ACTIVITIES_INTRO_STORAGE_KEY, 'true');
      }
    }
  }, [userHadNoWords, learningSession, experienceType]);

  const getIsItemDueForReview = () => {
    if (sessionType === SessionType.LEARN) {
      return false;
    }

    if (
      currentLearnableWithProgress?.progress?.growthLevel &&
      currentLearnableWithProgress.progress.growthLevel <= 6 &&
      sessionType !== SessionType.REVIEW
    ) {
      return false;
    }
    /* istanbul ignore next: please test in WEBSITE-7219 */
    if (learningSession?.card?.responseState === 'Correct') {
      return false;
    }

    return isPastNextDate(currentLearnableWithProgress?.progress?.nextDate);
  };

  const skippedLearnFeature = useRef(false);
  const skippingLearnFeature = useRef(false);
  /* istanbul ignore next: please test in WEBSITE-7219 */
  const trySkipLearnFeature = useCallback(async () => {
    if (!languagePairID || skippedLearnFeature.current) return false;

    skippingLearnFeature.current = true;

    try {
      await LearningQueueApi.skipLearningQueueFeatures(
        languagePairID,
        ['learn'],
        'all_words_marked_as_known',
      );
      skippedLearnFeature.current = true;
      return true;
    } catch (error) {
      console.error({ error });
      void safePush(ROUTES.scenarios.learn);
      return false;
    } finally {
      skippingLearnFeature.current = false;
    }
  }, [languagePairID, safePush]);

  /* istanbul ignore next: please test in WEBSITE-7219 */
  useEffect(() => {
    if (
      experienceType === '2025' &&
      learningSession.stage === 'summary' &&
      learnablesWithProgressLength === ignoredItemsCount &&
      !skippingLearnFeature.current &&
      !skippedLearnFeature.current
    ) {
      void trySkipLearnFeature();
    }
  }, [
    experienceType,
    learningSession.stage,
    learnablesWithProgressLength,
    ignoredItemsCount,
    trySkipLearnFeature,
  ]);

  const [couldShowRepetitionModal, setCouldShowRepetitionModal] = useState(false);
  useEffect(() => {
    if (
      !couldShowRepetitionModal &&
      sessionType === SessionType.LEARN &&
      learnablesWithProgress.some(l => !!l.progress) &&
      getLocalStorageEntry(SHOW_REPETITION_INTRO_MODAL_STORAGE_KEY) === 'true'
    ) {
      setCouldShowRepetitionModal(true);
    }
  }, [learnablesWithProgress, couldShowRepetitionModal, sessionType]);

  const leaveReview = () =>
    safePush(
      stringifyUrl(ROUTES.aprender.selectReview, {
        back: query.back ? String(query.back) : ROUTES.myWords,
      }),
    );

  const loadingError =
    /* istanbul ignore next: please test in WEBSITE-7219 */ error || activityError;
  const blobChromeLoading = hideClassicSessionChrome && !loadingError;

  /* istanbul ignore next: please test in WEBSITE-7219 */
  if (!isDataLoaded || isLocked || blobReview.isExhausted) {
    return (
      <>
        {blobChromeLoading ? (
          <BlobSessionLoadingSkeleton title={title} />
        ) : (
          <BaseSession
            sendCurrentLearningProgress={sendCurrentLearningProgress}
            loading
            title={title}
            error={loadingError}
            sessionType={sessionType}
            analyticsSessionId={analyticsSessionId}
            hideHeader={hideClassicSessionChrome}
          />
        )}
        {
          /* istanbul ignore next: please test in WEBSITE-7219 */
          blobReview.isExhausted ? (
            <BlobExperienceUpsell
              variant="review"
              wordsLeftToReview={blobReview.wordsLeftToReview}
              reviewLimitTrigger={
                blobReview.hasConsumedReviewedWord ? 'mid_session' : 'session_start'
              }
              isTrackingReady={!blobReview.isLoadingWordsLeftToReview}
              onClose={() => {
                void leaveReview();
              }}
              onSecondary={() => {
                const languagePairId = getLanguagePairId();
                void safePush(
                  stringifyUrl(ROUTES.aprender.learn, {
                    ...(languagePairId != null ? { language_pair_id: languagePairId } : {}),
                    source: 'blob_review_upsell',
                  }),
                );
              }}
            />
          ) : (
            isLocked && (
              <FeatureUpsellModal
                tokenType={tokenType as FeatureTokenType}
                onClose={() => {
                  void leaveReview();
                }}
              />
            )
          )
        }
      </>
    );
  }

  if (
    sessionType === SessionType.SPEED_REVIEW &&
    learningSession.stage === 'card' &&
    learningSession.card &&
    learningSession.card.card.kind === 'TestCard'
  ) {
    return (
      <>
        <DevTools page="learningSession" sessionType={sessionType} />
        <SpeedReview
          error={error}
          numCorrectAnswers={numCorrectAnswers}
          numIncorrectAnswers={numIncorrectAnswers}
          onFeedbackFinished={onFeedbackFinished}
          onIntroCountdownFinished={onIntroCountdownFinished}
          percentageTestTimeLeft={percentageTestTimeLeft}
          sendCurrentLearningProgress={sendCurrentLearningProgress}
          title={title}
          card={learningSession.card}
          learningSessionDispatch={learningSessionDispatch}
          analyticsSessionId={analyticsSessionId}
        />
      </>
    );
  }

  if (
    learningSession.stage === 'card' &&
    learningSession.card &&
    learningSession.card.data &&
    sessionType !== SessionType.SPEED_REVIEW
  ) {
    const { learnable } = learningSession.learnableWithProgress;
    const { id: learnableId } = learnable;

    const { AUDIO, VIDEO, REVIEW, LEARN, DIFFICULT_WORDS, MIXED } = SessionType;
    /* istanbul ignore next: make TS happy */
    if (![AUDIO, VIDEO, REVIEW, LEARN, DIFFICULT_WORDS, MIXED].includes(sessionType)) {
      throw new Error(`${sessionType} is not a ReviewSessionType`);
    }

    const sessionProps: StandardSessionProps = {
      languagePairId: languagePairID as number,
      sessionType,
      error,
      growthLevel,
      itemDueForReview: getIsItemDueForReview(),
      learnableId,
      onFeedbackFinished,
      progressSession,
      sendCurrentLearningProgress,
      sessionPercentComplete,
      title,
      currentLearnableProgressPreferences,
      learningSessionDispatch,
      card: learningSession.card,
      onItemIgnored,
      onItemDifficultyChanged,
      isFirstSession: !currentLearnableAttempts && !learningSession.learnableWithProgress.progress,
      couldShowRepetitionModal,
      markPreviousAnswerCorrect,
      userScore,
      // @ts-ignore: Property 'definition' exists
      definition: learnable.screens[0]?.definition,
      // @ts-ignore: Property 'item' exists
      item: learnable.screens[0]?.item,
      sessionSourceType,
      sessionSourceId,
      analyticsSessionId,
      isFromSaved: canLearnSavedFirst && (!!learnable.isFromSaved || isSaved(learnableId)),
      isSecondLearnable: learnablesWithProgress[1]?.learnable.id === learnableId,
    };

    return (
      <>
        <DevTools page="learningSession" sessionType={sessionType} />
        {isBlobSession ? (
          <BlobSession
            {...sessionProps}
            learnable={{ ...learnable, isFromSaved: sessionProps.isFromSaved }}
            sessionItemCount={learningSession.session.itemCount}
            onItemSkipped={onItemSkipped}
            onHoldFeedback={cancelDelayTimer}
            isDueForReview={isPastNextDate(currentLearnableWithProgress?.progress?.nextDate)}
            sessionSourceName={sessionSourceName}
          />
        ) : (
          <StandardSession {...sessionProps} />
        )}
      </>
    );
  }

  if (learningSession.stage === 'summaryLoading') {
    if (hideClassicSessionChrome && !error) {
      return <BlobSessionLoadingSkeleton title={title} />;
    }

    return (
      <BaseSession
        sendCurrentLearningProgress={sendCurrentLearningProgress}
        loading
        title={title}
        error={error}
        sessionType={sessionType}
        analyticsSessionId={analyticsSessionId}
        hideHeader={hideClassicSessionChrome}
      />
    );
  }
  /* istanbul ignore else: see message at end of component:  https://memrise.atlassian.net/browse/WEBSITE-4362 */
  if (learningSession.stage === 'summary') {
    /* istanbul ignore else: untested branch of code, please test */
    if (isDoneUpdatingProgress) {
      if (followLocals.pending) return null;
      if (followLocals.show && shownVideosRef.current.length > 0) {
        return (
          <FollowLocals
            sessionVideos={shownVideosRef.current}
            onContinue={followLocals.dismiss}
            onNotNow={followLocals.dismiss}
          />
        );
      }

      return (
        <EndOfSession
          pathwayNodeId={pathwayNodeId}
          summaryItems={learningSession.summary.summaryItems}
          hasReviewQueue={!!learningSession.summary.nextSession.singleContinue}
          levelName={levelName || learningSession.summary?.userScenario?.name || ''}
          iconSrc={learningSession.summary?.userScenario?.iconUrl || ''}
          hasUserScenario={!!learningSession.summary?.userScenario}
          isPremiumScenario={!!learningSession.summary?.userScenario?.isPremium}
          sessionType={sessionType}
          progressPreferences={progressPreferences}
          userScenarioCompleted={userScenarioCompleted}
          progressBefore={progressBefore}
          pointsBefore={pointsBefore}
          languageName={learningSession.summary.userPath?.name || ''}
          dailyNewWords={learningSession.summary.dailyNewWords}
          isBlobExperience={isBlobSession}
          completedWordlists={completedWordlists}
          dailyNewWordsBefore={dailyNewWordsBefore}
          analyticsSessionId={analyticsSessionId}
          sourceLanguageName={
            /* @ts-ignore: Property 'learnableWithProgress' exists */ learningSession
              ?.learnableWithProgress?.learnable?.screens[0]?.definition?.label || ''
          }
        />
      );
    } else {
      if (hideClassicSessionChrome && !error) {
        return <BlobSessionLoadingSkeleton title={title} />;
      }

      return (
        <BaseSession
          title={title}
          error={error}
          loading
          sendCurrentLearningProgress={sendCurrentLearningProgress}
          sessionType={sessionType}
          analyticsSessionId={analyticsSessionId}
          hideHeader={hideClassicSessionChrome}
        />
      );
    }
  }
  // Note that the type system does not guarantee that
  // something will be returned from the above if-clauses
  // but this is, in fact, the case: learningSession.stage
  // must either be 'card', 'loading', or 'summary' and
  // 'card' cannot be reached without there also being
  // a prompt, and response.
  /* istanbul ignore next */
  throw new Error(
    `You missed a case for SessionContent. ${[
      sessionType,
      learningSession.stage,
      currentLearnableWithProgress?.learnable.id,
      learningSession.card?.card?.kind,
      learningSession.card?.data?.kind,
    ]}`,
  );
};

/* istanbul ignore next: handled via useAutoplayWrapper in test utils */
const WithAutoPlay = (props: SessionContentProps) => (
  <AutoPlayProvider>
    <AudioProvider>
      <RestrictToAuth>
        <SessionContent {...props} />
      </RestrictToAuth>
    </AudioProvider>
  </AutoPlayProvider>
);

export default WithAutoPlay;
